/**
 * Platform Admin — люди платформы: пользователи и администраторы (AK-572).
 *
 * Пункты меню «Пользователи» и «Админы» вели в 404, хотя бэкенд готов
 * полностью (аудит админки 12.08). Здесь — контракт этих ручек:
 *
 *   GET    /api/v1/platform/users?search=&is_active=&limit=   → PlatformUser[]
 *   POST   /api/v1/platform/users/{id}/ban | /unban
 *   GET    /api/v1/platform/admins                            → PlatformAdmin[]
 *   POST   /api/v1/platform/admins                            → PlatformAdmin
 *   PATCH  /api/v1/platform/admins/{id}                       → PlatformAdmin
 *   DELETE /api/v1/platform/admins/{id}
 *
 * Реестр админов и любые изменения в нём — только для `superadmin`; остальным
 * сервер отвечает 403, и экран показывает это словами, а не пустой таблицей.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

/** `platform.PlatformUserRow`. */
export interface PlatformUser {
  id: string;
  email: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  platform_role: string | null;
  last_login_at: string | null;
  created_at: string;
  tenants_count: number;
}

/** `platform.AdminRow`. */
export interface PlatformAdmin {
  user_id: string;
  email: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  platform_role: PlatformRole;
  is_active: boolean;
  last_login_at: string | null;
  granted_at: string | null;
  granted_by_name: string | null;
}

export type PlatformRole = 'superadmin' | 'admin' | 'support';

/** Что роль означает на практике — иначе выбор в селекте вслепую. */
export const PLATFORM_ROLE_OPTIONS: {
  value: PlatformRole;
  label: string;
  hint: string;
}[] = [
  {
    value: 'superadmin',
    label: 'Суперадмин',
    hint: 'Всё, включая назначение других администраторов',
  },
  {
    value: 'admin',
    label: 'Администратор',
    hint: 'Компании, пользователи, справочники; админов не назначает',
  },
  {
    value: 'support',
    label: 'Поддержка',
    hint: 'Входит в компании только на чтение',
  },
];

export function platformRoleLabel(role: string | null | undefined): string {
  if (!role) return '—';
  return PLATFORM_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
}

export const platformPeopleKeys = {
  users: (search: string, onlyBlocked: boolean) =>
    ['platform-admin', 'users', search, onlyBlocked] as const,
  admins: () => ['platform-admin', 'admins'] as const,
  me: () => ['platform-admin', 'me'] as const,
};

/** `GET /platform/me` — своя роль; от неё зависит, что показывать. */
export function usePlatformMe(): UseQueryResult<
  { user_id: string; platform_role: PlatformRole },
  Error
> {
  return useQuery({
    queryKey: platformPeopleKeys.me(),
    queryFn: () =>
      api.get<{ user_id: string; platform_role: PlatformRole }>(
        '/api/v1/platform/me',
      ),
    staleTime: 5 * 60_000,
  });
}

export function usePlatformUsers(
  search: string,
  onlyBlocked: boolean,
): UseQueryResult<PlatformUser[], Error> {
  return useQuery({
    queryKey: platformPeopleKeys.users(search, onlyBlocked),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '500' });
      if (search.trim()) params.set('search', search.trim());
      // Фильтр «только заблокированные» — серверный: список режется лимитом,
      // и клиентская фильтрация показывала бы «ни одного заблокированного»
      // просто потому, что они не попали в первые 500 строк.
      if (onlyBlocked) params.set('is_active', 'false');
      return api.get<PlatformUser[]>(`/api/v1/platform/users?${params}`);
    },
    staleTime: 30_000,
  });
}

/** Глобальная блокировка пользователя — закрывает вход во ВСЕ компании. */
export function useSetUserActive(): UseMutationResult<
  void,
  Error,
  { userId: string; blocked: boolean; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, blocked, reason }) =>
      api.post<void>(
        `/api/v1/platform/users/${userId}/${blocked ? 'ban' : 'unban'}`,
        blocked ? { reason: reason ?? null } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-admin', 'users'] });
      void qc.invalidateQueries({ queryKey: platformPeopleKeys.admins() });
    },
  });
}

export function usePlatformAdmins(
  enabled = true,
): UseQueryResult<PlatformAdmin[], Error> {
  return useQuery({
    queryKey: platformPeopleKeys.admins(),
    queryFn: () => api.get<PlatformAdmin[]>('/api/v1/platform/admins'),
    enabled,
    // 403 «не суперадмин» повторять бессмысленно — права от повтора не растут.
    retry: false,
    staleTime: 60_000,
  });
}

export function useCreatePlatformAdmin(): UseMutationResult<
  PlatformAdmin,
  Error,
  { email: string; role: PlatformRole; firstName?: string; lastName?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role, firstName, lastName }) =>
      api.post<PlatformAdmin>('/api/v1/platform/admins', {
        email: email.trim().toLowerCase(),
        role,
        first_name: firstName?.trim() || null,
        last_name: lastName?.trim() || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformPeopleKeys.admins() });
    },
  });
}

export function useUpdatePlatformAdminRole(): UseMutationResult<
  PlatformAdmin,
  Error,
  { userId: string; role: PlatformRole }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }) =>
      api.patch<PlatformAdmin>(`/api/v1/platform/admins/${userId}`, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformPeopleKeys.admins() });
    },
  });
}

/** Снятие платформенной роли. Сам пользователь остаётся, роль уходит. */
export function useRevokePlatformAdmin(): UseMutationResult<
  void,
  Error,
  { userId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) =>
      api.delete<void>(`/api/v1/platform/admins/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformPeopleKeys.admins() });
    },
  });
}
